import { Workflow as WorkflowIcon } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import type { WorkflowView } from '../types'

export function WorkflowListCard({ workflow }: { workflow: WorkflowView }) {
  const { toTask } = useAppNavigate()

  const handleNavigate = () => {
    // Pass the title via nav state so the opened tab shows a real name
    // immediately instead of the "Workflow (<slug>)" placeholder.
    toTask(`/workflows/${workflow.slug}`, {
      replace: false,
      state: { workflowTitle: workflow.title },
    })
  }

  // An em dash rather than "0 sessions" when Hoku has no row for the workflow:
  // its sessions are counted from a table this page can't reach without one, so
  // zero would be a claim rather than a count.
  const sessionCount =
    workflow.sessionCount === null
      ? '—'
      : workflow.sessionCount.toLocaleString('en-US')

  return (
    // Whole-card click affordance via the stretched-link pattern (mirrors
    // DutyListCard): the title button's `after:absolute after:inset-0` overlay
    // extends its hit area to the whole card.
    <Card className="relative flex-row items-center gap-2 px-5 py-5 transition-shadow hover:shadow-lg">
      <div className="flex min-w-0 items-start gap-4">
        <WorkflowIcon
          size={22}
          strokeWidth={1.5}
          className="mt-0.5 shrink-0 text-primary"
        />
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleNavigate}
              className="cursor-pointer truncate text-left text-base font-normal text-primary-high-contrast after:absolute after:inset-0 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              {workflow.title}
            </button>
            {/* Retired in the nova repo: the row is here so its sessions stay
                reachable, so say why rather than let it read as runnable. */}
            {workflow.deprecated && (
              <Badge variant="destructive">Deprecated</Badge>
            )}
            {/* Every duty holding this workflow has it switched off, so it runs
                nowhere — see `WorkflowView.active`. */}
            {!workflow.active && <Badge variant="outline">Inactive</Badge>}
          </div>
          <div className="text-sm font-medium whitespace-nowrap text-muted-foreground">
            {sessionCount}{' '}
            {workflow.sessionCount === 1 ? 'session' : 'sessions'}
          </div>
        </div>
      </div>
    </Card>
  )
}
